import React from "react";
import { X } from "lucide-react";

const ReserveTransDetails = ({ isOpen, onClose, data, onApprove, onCancel, onExtend, isApproving, isCanceling }) => {
  if (!isOpen || !data) return null;

  const detailItem = (label, value) => (
    <div className="flex flex-col mb-4">
      <span className="text-xs tracking-wider text-gray-500 uppercase">{label}</span>
      <span className={`text-sm font-medium text-gray-900 ${value === data.user?.email ? 'truncate' : ''}`}>{value || "N/A"}</span>
    </div>
  );

  const formatNumber = (val) => {
    const num = Number(val);
    return isNaN(num) ? "0.00" : num.toLocaleString(undefined, { minimumFractionDigits: 2 });
  };

  const symbol = data.wallet?.currency?.symbol || "";

  return (
    <div className="flex fixed inset-0 z-50 justify-center items-center bg-black bg-opacity-50 transition-opacity">
      <div className="overflow-hidden h-[90vh] mx-4 w-full max-w-2xl bg-white rounded-2xl shadow-2xl transition-all transform">
        <div className="flex justify-between items-center px-6 py-4 bg-gray-50 border-b">
          <h3 className="text-lg font-semibold text-gray-900">Reserve Transaction Details</h3>
          <button onClick={onClose} className="text-gray-400 transition-colors hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-8 max-h-[80%] overflow-y-auto">
          <div className="grid grid-cols-1 gap-y-2 gap-x-8 md:grid-cols-2">
            {/* ─── Transaction Information ─── */}
            <div className="col-span-2 mb-6">
              <h4 className="pb-2 mb-4 text-sm font-bold tracking-wide text-emerald-800 uppercase border-b border-emerald-100">
                Transaction Information
              </h4>
              <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
                {detailItem("Transaction ID", data.uuid)}
                {detailItem("Reference", data.reference)}
                {detailItem("Status", data.status?.toUpperCase())}
                {detailItem("Type", data.type?.toUpperCase())}
                {detailItem("Service Type", data.serviceType?.replace(/_/g, " ")?.toUpperCase())}
                {detailItem("Payment Gateway", data.paymentGateway?.toUpperCase())}


                {detailItem("Amount", `${symbol} ${formatNumber(data.amount)}`)}
                {detailItem("Total Fees", `${symbol} ${formatNumber(data.totalFees)}`)}
                {detailItem("Platform Fee", `${symbol} ${formatNumber(data.platformFee)}`)}
                {detailItem("Gateway Fee", `${symbol} ${formatNumber(data.paymentGatewayFee)}`)}
                {detailItem("OTC Platform Fee", `${symbol} ${formatNumber(data.otcPlatformFee)}`)}

                {detailItem("Reserve Amount", `${symbol} ${formatNumber(data.reserveAmount)}`)}
                {detailItem("Reserve Balance Before", `${symbol} ${formatNumber(data.reserveBalanceBefore)}`)}
                {detailItem("Reserve Balance After", `${symbol} ${formatNumber(data.reserveBalanceAfter)}`)}
                {detailItem("Hold Days", data.reserveHoldDays !== null ? `${data.reserveHoldDays} days` : "N/A")}
                {detailItem("Reverse Release Date", data.reserveReleaseDate ? new Date(data.reserveReleaseDate).toLocaleString() : "N/A")}
                {detailItem("Reverse Release Status", data.reserveReleaseStatus || "N/A")}
                {detailItem("Transaction Spread", data.transactionSpread || "N/A")}
                {detailItem("Gateway Reference", data.paymentGatewayReference)}
                {detailItem("Date Created", data.created ? new Date(data.created).toLocaleString() : "N/A")}
              </div>
            </div>

            {/* ─── User Details ─── */}
            <div className="col-span-2 mb-6">
              <h4 className="pb-2 mb-4 text-sm font-bold tracking-wide text-emerald-800 uppercase border-b border-emerald-100">
                User Details
              </h4>
              <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
                {detailItem("Full Name", `${data.user?.firstName || ""} ${data.user?.lastName || ""}`.trim() || "N/A")}
                {detailItem("Email", data.user?.email)}
                {detailItem("Phone", data.user?.phoneNumber)}
                {detailItem("Country", data.user?.country)}
                {detailItem("User ID", data.user?.uuid)}
                {detailItem("KYC Level", data.user?.kycLevel?.toString())}
                {detailItem("Account Status", data.user?.isActive ? "Active" : "Inactive")}
                {detailItem("Email Verified", data.user?.isEmailVerified ? "Yes" : "No")}
                {detailItem("Phone Verified", data.user?.isPhoneVerified ? "Yes" : "No")}
                {detailItem("High Risk", data.user?.isHighRisk ? "Yes" : "No")}
                {detailItem("OTC Customer", data.user?.isOtcCustomer ? "Yes" : "No")}
                {detailItem("Reserve %", `${data.user?.reservePercentage}%`)}
                {detailItem("Referral Source", data.user?.referedBy)}
                {detailItem("Channel", data.user?.channel)}
              </div>
            </div>

            {/* ─── Wallet Details ─── */}
            <div className="col-span-2 mb-6">
              <h4 className="pb-2 mb-4 text-sm font-bold tracking-wide text-emerald-800 uppercase border-b border-emerald-100">
                Wallet Details
              </h4>
              <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
                {detailItem("Wallet ID", data.wallet?.uuid)}
                {detailItem("Wallet Balance", `${symbol} ${formatNumber(data.wallet?.balance)}`)}
                {detailItem("Reserve Balance", `${symbol} ${formatNumber(data.wallet?.reserveBalance)}`)}
              </div>
            </div>

            {/* ─── Currency Info ─── */}
            <div className="col-span-2 mb-6">
              <h4 className="pb-2 mb-4 text-sm font-bold tracking-wide text-emerald-800 uppercase border-b border-emerald-100">
                Currency Info
              </h4>
              <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
                {detailItem("Code", data.wallet?.currency?.currencyCode?.toUpperCase())}
                {detailItem("Symbol", data.wallet?.currency?.symbol)}
                {detailItem("Country", data.wallet?.currency?.country)}
                {detailItem("Spread Type", data.wallet?.currency?.spreadType)}
                {detailItem("Buy Spread", data.wallet?.currency?.buySpread)}
                {detailItem("Sell Spread", data.wallet?.currency?.sellSpread)}
                {detailItem("Payout Enabled", data.wallet?.currency?.payoutEnabled ? "Yes" : "No")}
                {detailItem("Collection Enabled", data.wallet?.currency?.collectionEnabled ? "Yes" : "No")}
                {detailItem("Min Funding", `${symbol} ${formatNumber(data.wallet?.currency?.minFundingAmount)}`)}
              </div>
            </div>

            {/* ─── Description ─── */}
            <div className="col-span-2 mt-1">
              <span className="block pb-2 mb-2 text-xs font-bold text-emerald-800 uppercase border-b border-emerald-100">
                Description / Note
              </span>
              <p className="text-sm italic leading-relaxed text-gray-700">
                {data.description || data.note || "No description provided"}
              </p>
            </div>
          </div>
        </div>

        <div className="flex justify-center items-center px-2 py-6 space-x-2 space-y-0 bg-gray-50 border-t md:justify-end md:space-y-0 md:space-x-4">
          <button
            onClick={onClose}
            className="p-2 font-bold text-gray-600 bg-white rounded-xl border-2 border-gray-200 shadow-sm transition-all md:py-3 md:px-5 md:text-sm hover:bg-gray-50 active:scale-95"
          >
            <span className="hidden md:block">Close</span>
            <span className="md:hidden"><X size={18} /></span>
          </button>

          {(data?.reserveReleaseStatus !== "released" && data?.reserveReleaseStatus !== "cancelled") && <button
            onClick={() => onCancel(data)}
            disabled={isCanceling}
            className={`p-2 text-xs font-bold text-red-600 bg-white rounded-xl border-2 border-red-500 shadow-sm transition-all md:py-3 md:px-5 md:text-sm hover:bg-red-50 active:scale-95 ${isCanceling ? "opacity-50 cursor-not-allowed" : ""}`}
          >
            {isCanceling ? "Canceling..." : "Cancel Reserve"}
          </button>}

          {(data?.reserveReleaseStatus !== "released" && data?.reserveReleaseStatus !== "cancelled") && <button
            onClick={() => onExtend(data)}
            className="p-2 text-xs font-bold text-yellow-600 bg-white rounded-xl border-2 border-yellow-500 shadow-sm transition-all md:py-3 md:px-5 md:text-sm hover:bg-yellow-100 active:scale-95"
          >
            Extend Reserve
          </button>}

          {(data?.reserveReleaseStatus !== "released" && data?.reserveReleaseStatus !== "cancelled" ) && <button
            onClick={() => onApprove(data)}
            disabled={isApproving}
            className={`p-2 text-xs font-bold text-white bg-emerald-600 rounded-xl border-2 border-emerald-500 shadow-sm transition-all md:py-3 md:px-5 md:text-sm active:scale-95 ${isApproving ? "opacity-50 cursor-not-allowed" : ""}`}
          >
            {isApproving ? "Approving..." : "Approve"}
          </button>}
        </div>
      </div>
    </div>
  );
};

export default ReserveTransDetails;
